import { inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { listPromotions$ } from '@vendasta/protos/promotions/v1/promotions-service';
import { PromotionStatus } from '@vendasta/protos/promotions/v1/enums';
import { FilterGroupOperator, FilterOperator } from '@vendasta/protos/galaxytypes/v1/enums';
import { PROMOTIONS_NAMESPACE_TOKEN } from '@galaxy/promotions/core';
import { ExtraCategoryOption } from '@galaxy/email-ui/builder-v3';
import { SubstitutionParameter } from '@vendasta/templates';

export const SELECTED_PROMOTION_FIELDS: SubstitutionParameter[] = [
  new SubstitutionParameter({ replacementTag: 'selectedPromotionName', displayName: 'Promotion name', requiredOptions: [], isHtml: false, isDeprecated: false }),
  new SubstitutionParameter({ replacementTag: 'selectedPromotionDescription', displayName: 'Promotion description', requiredOptions: [], isHtml: false, isDeprecated: false }),
  new SubstitutionParameter({ replacementTag: 'selectedPromotionStartDate', displayName: 'Promotion start date', requiredOptions: [], isHtml: false, isDeprecated: false }),
  new SubstitutionParameter({ replacementTag: 'selectedPromotionEndDate', displayName: 'Promotion end date', requiredOptions: [], isHtml: false, isDeprecated: false }),
  new SubstitutionParameter({ replacementTag: 'selectedPromotionOfferings', displayName: 'Promotion offering', requiredOptions: [], isHtml: false, isDeprecated: false }),
];

export function fetchActivePromotions$(namespace$: Observable<string>): Observable<ExtraCategoryOption[]> {
  return namespace$.pipe(
    switchMap((namespace) => {
      if (!namespace) return of([]);
      return listPromotions$({
        namespace,
        pagingOptions: { pageSize: 100 },
        filters: {
          operator: FilterGroupOperator.FILTER_GROUP_OPERATOR_AND,
          filters: [
            {
              fieldId: 'status',
              operator: FilterOperator.FILTER_OPERATOR_IS,
              values: [{ string: PromotionStatus.PROMOTION_STATUS_ACTIVE }],
              floatTolerance: 0,
            },
          ],
        },
      }).pipe(
        map((resp) =>
          (resp.promotions ?? [])
            .map((p) => ({
              value: p.promotionId ?? '',
              label: p.content?.title ?? p.promotionCode ?? '(untitled)',
            }))
            .filter((o) => !!o.value),
        ),
        catchError(() => of([] as ExtraCategoryOption[])),
      );
    }),
  );
}

export function activePromotionsFetcherFactory(): () => Observable<{ id: string; name: string }[]> {
  const namespaceToken = inject(PROMOTIONS_NAMESPACE_TOKEN);
  const namespace$ = typeof namespaceToken === 'string' ? of(namespaceToken) : (namespaceToken as Observable<string>);
  return () =>
    fetchActivePromotions$(namespace$).pipe(
      take(1),
      map((options) => options.map((o) => ({ id: o.value, name: o.label }))),
    );
}
